import { formatShortCurrency, formatCurrency } from "./chart_formatters"

// Shared Chart.js configs for the portfolio trend charts — used by BOTH
// the collapsible Trends strip (`portfolio_trend_charts_controller`) and
// the standalone Ask AI trend cards (`trend_chart_card_controller`), so
// the two renderings stay pixel-identical. `s` is the series payload:
// { months, burn: {planned, actual}, change_orders: {count, value},
//   health: {green, amber, red} }.

function commonOptions(override = {}) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 6,
          boxHeight: 6,
          font: { size: 13 },
          usePointStyle: true
        }
      },
      ...(override.plugins || {})
    },
    scales: override.scales || {}
  }
}

export function burnChartConfig(s) {
  return {
    type: "line",
    data: {
      labels: s.months,
      datasets: [
        {
          label: "Planned",
          data: s.burn.planned,
          borderColor: "#9ca3af",
          backgroundColor: "transparent",
          borderDash: [5, 4],
          borderWidth: 1.5,
          fill: false,
          pointRadius: 0,
          pointHoverRadius: 4,
          spanGaps: true,
          tension: 0.15
        },
        {
          label: "Actual",
          data: s.burn.actual,
          borderColor: "#2563eb",
          borderWidth: 2,
          fill: {
            target: 0,
            above: "rgba(239,68,68,0.18)",
            below: "rgba(59,130,246,0.14)"
          },
          pointRadius: 0,
          pointHoverRadius: 4,
          spanGaps: true,
          tension: 0.15
        }
      ]
    },
    options: commonOptions({
      scales: {
        y: {
          ticks: { callback: v => formatShortCurrency(v), font: { size: 13 } },
          grid: { color: "#f3f4f6" }
        },
        x: { ticks: { font: { size: 13 } }, grid: { display: false } }
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: ctx => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}`
          }
        }
      }
    })
  }
}

export function changeOrdersChartConfig(s) {
  return {
    type: "bar",
    data: {
      labels: s.months,
      datasets: [
        {
          label: "CO Value",
          data: s.change_orders.value,
          backgroundColor: "rgba(251,146,60,0.8)",
          borderWidth: 0,
          borderRadius: 3,
          maxBarThickness: 32
        }
      ]
    },
    options: commonOptions({
      scales: {
        y: {
          beginAtZero: true,
          ticks: { callback: v => formatShortCurrency(v), font: { size: 13 } },
          grid: { color: "#f3f4f6" }
        },
        x: { ticks: { font: { size: 13 } }, grid: { display: false } }
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: ctx => {
              const count = s.change_orders.count[ctx.dataIndex] || 0
              const value = formatCurrency(ctx.parsed.y)
              return `${value} · ${count} change order${count === 1 ? "" : "s"}`
            }
          }
        }
      }
    })
  }
}

export function healthChartConfig(s) {
  return {
    type: "bar",
    data: {
      labels: s.months,
      datasets: [
        {
          label: "On Track",
          data: s.health.green,
          backgroundColor: "#10b981",
          borderWidth: 0,
          borderRadius: 2,
          maxBarThickness: 32
        },
        {
          label: "At Risk",
          data: s.health.amber,
          backgroundColor: "#f59e0b",
          borderWidth: 0,
          borderRadius: 2,
          maxBarThickness: 32
        },
        {
          label: "Behind",
          data: s.health.red,
          backgroundColor: "#ef4444",
          borderWidth: 0,
          borderRadius: 2,
          maxBarThickness: 32
        }
      ]
    },
    options: commonOptions({
      scales: {
        x: { stacked: true, ticks: { font: { size: 13 } }, grid: { display: false } },
        y: {
          stacked: true,
          beginAtZero: true,
          ticks: { precision: 0, font: { size: 13 } },
          grid: { color: "#f3f4f6" }
        }
      }
    })
  }
}
